<script setup>
import { Head } from '@inertiajs/vue3'
import { computed, onMounted } from 'vue'

const props = defineProps({
  title: {
    type: String,
    required: false,
    default: 'Kulfood'
  },
  description: {
    type: String,
    required: false,
    default: 'Kulfood poskrbi, da si sredi dneva vzameš čas zase, dober obrok postane preprost, zanesljiv in prijeten.'
  },
  keywords: {
    type: String,
    required: false,
    default: 'Kulfood, kuhanje, jesti, dober obrok, čas za dober obrok, preprost, zanesljiv, prijeten'
  },
  canonical: {
    type: String,
    default: ''
  },
  image: {
    type: String,
    default: '/images/common/logo.svg'
  },
  type: {
    type: String,
    default: 'website'
  },
  publishedTime: {
    type: String,
    default: ''
  },
  modifiedTime: {
    type: String,
    default: ''
  },
  author: {
    type: String,
    default: 'Kulfood'
  },
  structuredData: {
    type: [Object, Array],
    default: null
  },
  robots: {
    type: String,
    default: 'index, follow'
  }
})

const siteName = 'Kulfood'
const siteUrl = 'https://kulfood.si'
const twitterHandle = '@kulfood_si'

const fullTitle = computed(() => {
  return props.title === siteName ? siteName : `${props.title}`
})

const canonicalUrl = computed(() => {
  return props.canonical ? `${siteUrl}${props.canonical}` : ''
})

const imageUrl = computed(() => {
  return props.image.startsWith('http') ? props.image : `${siteUrl}${props.image}`
})

const structuredDataArray = computed(() => {
  if (!props.structuredData) return []
  
  // If it's an array, return it; if it's a single object, wrap it in an array
  const dataArray = Array.isArray(props.structuredData) 
    ? props.structuredData 
    : [props.structuredData]
  
  // Add @context to each structured data object
  return dataArray.map(data => ({
    '@context': 'https://schema.org',
    ...data
  }))
})

// Inject structured data into head
onMounted(() => {
  structuredDataArray.value.forEach((data, index) => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.id = `structured-data-${index}`
    script.textContent = JSON.stringify(data)
    document.head.appendChild(script)
  })
})
</script>

<template>
  <Head :title="fullTitle">
    <!-- Basic Meta Tags -->
    <meta head-key="description" name="description" :content="description" />
    <meta v-if="keywords" head-key="keywords" name="keywords" :content="keywords" />
    <meta name="author" :content="author" />
    <meta name="robots" :content="robots" />
    <meta name="language" content="sl" />
    <meta name="revisit-after" content="7 days" />
    
    <!-- Open Graph Meta Tags -->
    <meta property="og:type" :content="type" />
    <meta property="og:title" :content="fullTitle" />
    <meta property="og:description" :content="description" />
    <meta property="og:image" :content="imageUrl" />
    <meta v-if="canonicalUrl" property="og:url" :content="canonicalUrl" />
    <meta property="og:site_name" :content="siteName" />
    <meta property="og:locale" content="sl_SI" />
    
    <!-- Article specific Open Graph tags -->
    <meta v-if="publishedTime" property="article:published_time" :content="publishedTime" />
    <meta v-if="modifiedTime" property="article:modified_time" :content="modifiedTime" />
    <meta v-if="author" property="article:author" :content="author" />
    
    <!-- Twitter Card Meta Tags -->
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:site" :content="twitterHandle" />
    <meta name="twitter:creator" :content="twitterHandle" />
    <meta name="twitter:title" :content="fullTitle" />
    <meta name="twitter:description" :content="description" />
    <meta name="twitter:image" :content="imageUrl" />
    
    <!-- Additional SEO Meta Tags -->
    <meta name="theme-color" content="#2563eb" />
    <meta name="msapplication-TileColor" content="#2563eb" />
    <meta name="apple-mobile-web-app-capable" content="yes" />
    <meta name="apple-mobile-web-app-status-bar-style" content="default" />
    <meta name="apple-mobile-web-app-title" :content="siteName" />
    
    <!-- Canonical URL -->
    <link v-if="canonicalUrl" rel="canonical" :href="canonicalUrl" />
  </Head>
</template>
